import Button from "@edaz/edazkit/Button/index";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { IconType } from "@edaz/edazkit/Icon";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView } from "@frui.ts/views";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import type ExternalReportResponse from "entities/externalReportResponse";
import ReportRequestStatus from "entities/reportRequestStatus";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import { busyIdentificator } from "utils/busyWatcher";
import { formatDateTime, translateEnum, translateGeneral } from "utils/localizations";
import CzechStatisticalOfficeReportsListViewModel from "viewModels/timeCharges/reporting/externalReports/czechStatisticalOfficeReports/czechStatisticalOfficeReportsListViewModel";
import ListView from "views/common/list";

const CzechStatisticalOfficeReportsListView: ViewComponent<CzechStatisticalOfficeReportsListViewModel> = observer(({ vm }) => {
  const isDownloadBusy = vm.busyWatcher.checkBusy(busyIdentificator([vm.request.from, vm.request.to]));

  const columns: ColumnDefinition<ExternalReportResponse>[] = [
    {
      title: vm.translateAttribute("reportName"),
      property: "reportName",
    },
    {
      title: vm.translateAttribute("processedAt"),
      valueFormatter: props => formatDateTime(props.value),
      property: "processedAt",
    },
    {
      title: vm.translateAttribute("status"),
      property: "status",
      valueFormatter: props => translateEnum("reportRequestStatus", props.value),
    },
  ];

  const actionButtons = (item: ExternalReportResponse) => {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([item]));

    return (
      <>
        {item.status === ReportRequestStatus.Processed && (
          <Button
            variant="success"
            size="sm"
            className="rounded"
            onClick={() => vm.onDownload(item)}
            disabled={isBusy}
            iconLeft={isBusy ? IconType.Spinner : IconType.Download}>
            {vm.translate("downloadFile")}
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      {vm.canMakeReport && (
        <Form className="mb-4 p-4 bg-light rounded" onSubmit={preventDefault(vm.onFormSubmit)}>
          <Row>
            <FormGroup as={Col} sm={6} md={4} target={vm.request} property="from">
              <FormLabel>{vm.translate("fromQuarter")}</FormLabel>
              <AdvancedSelect placeholder={translateGeneral("choose")} allowEmpty={true} items={vm.quarterOptions} />
            </FormGroup>
            <FormGroup as={Col} sm={6} md={4} target={vm.request} property="to">
              <FormLabel>{vm.translate("toQuarter")}</FormLabel>
              <AdvancedSelect placeholder={translateGeneral("choose")} allowEmpty={true} items={vm.quarterOptionsTo} />
            </FormGroup>
            <Form.Group as={Col} md="auto">
              <Button
                className="mt-md-5 rounded d-block"
                type="submit"
                disabled={isDownloadBusy}
                iconLeft={isDownloadBusy ? IconType.Spinner : undefined}>
                {translateGeneral("generateReport")}
              </Button>
            </Form.Group>
          </Row>
        </Form>
      )}
      <ListView vm={vm} columns={columns} showDetailWithList={true} hideTitle={true} actionButtons={actionButtons} />
    </>
  );
});

registerView(CzechStatisticalOfficeReportsListView, CzechStatisticalOfficeReportsListViewModel);
